import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { UsageMeter } from "@/components/UsageMeter";
import { Check, Sparkles, Users, Info, CreditCard, Loader2, ShieldCheck, Crown, Gem, Star, Zap, Rocket, Volume2, Gift, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TIER_META = {
  free: { icon: Sparkles, accent: "text-muted-foreground", ring: "", badge: null },
  omni_plus: { icon: Star, accent: "text-primary", ring: "ring-2 ring-primary", badge: "Most Popular" },
  duo: { icon: Users, accent: "text-green-600", ring: "ring-2 ring-green-400", badge: null },
  platinum: { icon: Crown, accent: "text-amber-500", ring: "ring-2 ring-amber-400", badge: "Best Value" },
  diamond: { icon: Gem, accent: "text-violet-500", ring: "ring-2 ring-violet-400", badge: "Group" },
};

const ADDON_META = {
  extras_limited: { icon: Zap, accent: "text-blue-500", gradient: "from-blue-500 to-cyan-500" },
  extras_full: { icon: Rocket, accent: "text-violet-500", gradient: "from-violet-500 to-fuchsia-500" },
  tts_unlimited: { icon: Volume2, accent: "text-emerald-500", gradient: "from-emerald-500 to-teal-500" },
  voice_100: { icon: Volume2, accent: "text-emerald-500", gradient: "from-emerald-500 to-teal-500" },
  voice_250: { icon: Volume2, accent: "text-teal-500", gradient: "from-teal-500 to-cyan-600" },
  voice_max: { icon: Volume2, accent: "text-cyan-600", gradient: "from-cyan-600 to-sky-600" },
  global_voice_pack: { icon: Globe, accent: "text-indigo-500", gradient: "from-indigo-500 to-purple-500" },
};

export default function PricingPage() {
  const { token, user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState(null);
  const [addOns, setAddOns] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [checkout, setCheckout] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [provider, setProvider] = useState("stripe");
  const [providers, setProviders] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTiers(); fetchProviders(); }, []);

  const fetchTiers = async () => {
    try {
      const { data } = await axios.get(`${API}/pricing/tiers`);
      setTiers(data);
      setAddOns(data.add_ons || null);
    } catch (err) { console.error(err); }
  };

  const fetchProviders = async () => {
    try {
      const { data } = await axios.get(`${API}/payment/providers`);
      setProviders(data.providers || []);
      if (data.providers?.length && !data.providers.some(p => p.key === "stripe")) {
        setProvider(data.providers[0].key);
      }
    } catch { /* providers optional */ }
  };

  const startCheckout = async (tierKey) => {
    if (user?.tier === tierKey) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/payment/initialize`, {
        tier: tierKey, billing, provider,
        origin_url: window.location.origin,
      }, { headers });
      if (data.status === "redirect" && data.checkout_url) {
        // Real Stripe checkout — redirect to hosted page
        window.location.href = data.checkout_url;
      } else if (data.status === "redirect" && data.authorization_url) {
        // Paystack redirect
        window.location.href = data.authorization_url;
      } else {
        // Mock flow — show confirm modal
        setCheckout(data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); }
    finally { setProcessing(false); }
  };

  const confirmPayment = async () => {
    if (!checkout?.reference) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/payment/verify?reference=${checkout.reference}`, {}, { headers });
      if (data.status === "success" || data.status === "already_verified") {
        updateUser({ tier: data.tier });
        toast.success(`Welcome to ${tiers?.tiers[data.tier]?.name || data.tier}!`);
        setCheckout(null);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Verification failed"); }
    finally { setProcessing(false); }
  };

  const subscribeAddon = async (pkgId) => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/add-ons/checkout`, { package_id: pkgId, origin_url: window.location.origin }, { headers });
      if (data.status === "redirect" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.status === "subscribed") {
        toast.success(data.message || "Add-on activated!");
        updateUser({ active_addon: pkgId });
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to start add-on checkout"); }
    finally { setProcessing(false); }
  };

  const buyTopup = async (packId) => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/topups/checkout`, { pack_id: packId, origin_url: window.location.origin }, { headers });
      if (data.status === "redirect" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.status === "credited") {
        toast.success(data.message || "Pack credited!");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Top-up checkout failed"); }
    finally { setProcessing(false); }
  };

  const tierOrder = ["free", "omni_plus", "duo", "platinum", "diamond"];
  const showAddOns = addOns && ["omni_plus", "duo", "platinum", "diamond", "team_dev"].includes(user?.tier);

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
          <div className="text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-medium mb-3">Choose your path</h1>
            <p className="text-muted-foreground">Start free. Grow when you&apos;re ready.</p>
            <div className="flex items-center justify-center gap-3 mt-6">
              <button data-testid="billing-monthly" onClick={() => setBilling("monthly")}
                className={cn("px-4 py-2 rounded-full text-sm transition-all", billing === "monthly" ? "bg-primary text-primary-foreground" : "bg-muted")}>
                Monthly
              </button>
              <button data-testid="billing-annual" onClick={() => setBilling("annual")}
                className={cn("px-4 py-2 rounded-full text-sm transition-all", billing === "annual" ? "bg-primary text-primary-foreground" : "bg-muted")}>
                Annual <span className="text-xs opacity-80">(save 5%)</span>
              </button>
            </div>

            {/* Payment Provider Selector */}
            {providers.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <span className="text-xs text-muted-foreground mr-1">Pay via:</span>
                {providers.map(p => (
                  <button
                    key={p.key}
                    data-testid={`provider-${p.key}`}
                    onClick={() => setProvider(p.key)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all",
                      provider === p.key
                        ? "bg-foreground text-background border-foreground"
                        : "border-border/50 text-muted-foreground hover:bg-muted/50"
                    )}
                  >
                    <span className={cn("w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold text-white",
                      p.key === "stripe" ? "bg-violet-600" : p.key === "razorpay" ? "bg-blue-600" : "bg-teal-600"
                    )}>
                      {p.key.charAt(0).toUpperCase()}
                    </span>
                    {p.name}
                    <span className="text-[9px] text-muted-foreground/50">({p.currency})</span>
                    {p.mock && <span className="text-[8px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">DEMO</span>}
                    {!p.mock && <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">LIVE</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Gift CTA */}
          <div className="text-center mt-2">
            <button
              data-testid="gift-subscription-link"
              onClick={() => navigate("/gift")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 text-white text-sm font-medium hover:opacity-90 transition-all shadow-lg shadow-rose-500/20"
            >
              <Gift className="w-4 h-4" /> Gift a Subscription
            </button>
          </div>

          {/* Usage Meter (logged-in, v2 pricing) */}
          {user && <div className="max-w-2xl mx-auto"><UsageMeter /></div>}

          {/* Tier Cards */}
          {tiers && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {tierOrder.map(tierKey => {
                const tier = tiers.tiers[tierKey];
                if (!tier) return null;
                const price = billing === "annual" ? tier.price_annual : tier.price_monthly;
                const isCurrentPlan = user?.tier === tierKey;
                const meta = TIER_META[tierKey] || TIER_META.free;
                const Icon = meta.icon;
                const hasAddOns = tier.add_ons_available;

                return (
                  <div key={tierKey} data-testid={`tier-card-${tierKey}`} className={cn("card-omni p-6 flex flex-col relative", meta.ring)}>
                    {meta.badge && (
                      <span className={cn("absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-card border border-border/50 shadow-sm ui-label", meta.accent)}>
                        {meta.badge}
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-3 mt-1">
                      <div className="w-8 h-8 rounded-xl bg-muted/50 flex items-center justify-center">
                        <Icon className={cn("w-4 h-4", meta.accent)} strokeWidth={1.5} />
                      </div>
                      <h3 className="font-heading text-lg font-medium">{tier.name}</h3>
                    </div>
                    <div className="mb-4">
                      <span className="font-heading text-2xl font-medium">${price}</span>
                      {price > 0 && <span className="text-muted-foreground text-xs">/{billing === "annual" ? "yr" : "mo"}</span>}
                    </div>
                    <ul className="space-y-2 flex-1 mb-4">
                      {tier.features.map(f => (
                        <li key={f} className="flex items-start gap-1.5 text-xs leading-relaxed">
                          <Check className={cn("w-3.5 h-3.5 flex-shrink-0 mt-0.5", meta.accent)} strokeWidth={2} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {hasAddOns && (
                      <p className="text-[10px] text-center text-muted-foreground mb-2 font-medium">
                        <Zap className="w-3 h-3 inline mr-0.5" />Extras add-ons available (+$7.99 / +$14.99)
                      </p>
                    )}
                    {tier.wisdom_tts_monthly_limit != null && (
                      <p className="text-[10px] text-center text-muted-foreground mb-2 flex items-center justify-center gap-1">
                        <Volume2 className="w-3 h-3 inline" />
                        {tier.wisdom_tts_monthly_limit === -1 ? "Unlimited" : tier.wisdom_tts_monthly_limit} Wisdom voice{tier.wisdom_tts_monthly_limit !== 1 ? "s" : ""}/mo
                        {tier.wisdom_tts_monthly_limit !== -1 && <span className="text-muted-foreground/60">| Voice plans from $8.99</span>}
                      </p>
                    )}
                    <Button data-testid={`select-${tierKey}-btn`} onClick={() => startCheckout(tierKey)}
                      disabled={isCurrentPlan || processing || tierKey === "free"}
                      className={cn("w-full rounded-full text-sm",
                        isCurrentPlan ? "bg-muted text-muted-foreground" :
                        tierKey === "diamond" ? "bg-violet-600 text-white hover:bg-violet-700" :
                        tierKey === "platinum" ? "bg-amber-500 text-white hover:bg-amber-600" :
                        meta.ring ? "bg-primary text-primary-foreground" :
                        "bg-muted text-foreground hover:bg-primary hover:text-primary-foreground"
                      )}>
                      {isCurrentPlan ? "Current Plan" : tierKey === "free" ? "Free Forever" : "Upgrade"}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add-On Packages Section */}
          {showAddOns && addOns && (
            <div className="max-w-3xl mx-auto" data-testid="addon-section">
              <div className="text-center mb-5">
                <h2 className="font-heading text-xl font-medium">Power Up Your Plan</h2>
                <p className="text-sm text-muted-foreground mt-1">Add premium features to your {tiers?.tiers[user?.tier]?.name || "plan"}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(addOns).map(([pkgId, pkg]) => {
                  const meta = ADDON_META[pkgId] || ADDON_META.extras_limited;
                  const Icon = meta.icon;
                  const activeAddons = user?.active_addons || [];
                  const isActive = user?.active_addon === pkgId || activeAddons.includes(pkgId);
                  const addonPrice = billing === "annual" ? pkg.price_annual : pkg.price_monthly;

                  return (
                    <div key={pkgId} data-testid={`addon-card-${pkgId}`}
                      className={cn("p-5 rounded-2xl border-2 transition-all", isActive ? "border-primary bg-primary/5" : "border-border/40 hover:border-border")}>
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className={cn("w-9 h-9 rounded-xl bg-gradient-to-br flex items-center justify-center", meta.gradient)}>
                          <Icon className="w-4.5 h-4.5 text-white" strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm">{pkg.name}</h3>
                          <p className="text-[10px] text-muted-foreground">{pkg.tagline}</p>
                        </div>
                      </div>
                      <div className="mb-3">
                        <span className="font-heading text-xl font-medium">+${addonPrice}</span>
                        <span className="text-muted-foreground text-xs">/{billing === "annual" ? "yr" : "mo"}</span>
                      </div>
                      <ul className="space-y-1.5 mb-4">
                        {pkg.features.map(f => (
                          <li key={f} className="flex items-start gap-1.5 text-[11px] leading-relaxed">
                            <Check className={cn("w-3 h-3 flex-shrink-0 mt-0.5", meta.accent)} strokeWidth={2} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <Button data-testid={`addon-${pkgId}-btn`} onClick={() => subscribeAddon(pkgId)}
                        disabled={isActive || processing}
                        className={cn("w-full rounded-full text-xs", isActive ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground")}>
                        {isActive ? "Active" : `Add for +$${pkg.price_monthly}/mo`}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top-Up Store */}
          {user && tiers?.top_ups && (
            <div className="max-w-3xl mx-auto" data-testid="topup-store">
              <div className="text-center mb-5">
                <h2 className="font-heading text-xl font-medium">Top-Up Store</h2>
                <p className="text-sm text-muted-foreground mt-1">One-time packs — never hit a wall mid-flow</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Object.entries(tiers.top_ups).map(([packId, pack]) => (
                  <button
                    key={packId}
                    data-testid={`topup-card-${packId}`}
                    onClick={() => buyTopup(packId)}
                    disabled={processing}
                    className="p-4 rounded-2xl border-2 border-border/40 hover:border-primary/60 hover:bg-primary/5 transition-all text-left group"
                  >
                    <p className="font-semibold text-sm group-hover:text-primary transition-colors">{pack.name}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{pack.tagline}</p>
                    <p className="font-heading text-lg font-medium mt-2">${pack.price}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* AI Support Disclaimer */}
          <div className="p-6 bg-muted/50 border border-border/50 rounded-[2rem] max-w-2xl mx-auto">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <h3 className="font-semibold mb-2 text-sm">AI-Powered Support Only</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  All customer support across every plan is provided <strong>exclusively via AI-powered responses</strong>.
                </p>
                <p className="text-xs text-muted-foreground border-l-2 border-warning/50 pl-3">
                  <strong>Disclaimer:</strong> AI responses do not constitute professional medical, legal, or financial advice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      {checkout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setCheckout(null)}>
          <div className="w-full max-w-md animate-fade-in" onClick={e => e.stopPropagation()}>
            <div className="card-omni p-8 bg-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-medium">Checkout</h2>
                  <p className="text-sm text-muted-foreground">{tiers?.tiers[checkout.tier]?.name || checkout.tier}</p>
                </div>
              </div>
              <div className="p-5 bg-muted/50 rounded-2xl mb-6 space-y-3">
                <div className="flex justify-between text-sm"><span>Plan</span><span className="font-semibold">{tiers?.tiers[checkout.tier]?.name}</span></div>
                <div className="flex justify-between text-sm"><span>Billing</span><span className="font-semibold capitalize">{billing}</span></div>
                <div className="flex justify-between text-sm"><span>Provider</span><span className="font-semibold capitalize">{checkout.provider_name || checkout.provider || "Paystack"}</span></div>
                <div className="flex justify-between text-sm"><span>Currency</span><span className="font-semibold">{checkout.currency || "NGN"}</span></div>
                <div className="border-t border-border/50 pt-3 flex justify-between"><span className="font-medium">Total</span><span className="font-heading text-xl font-medium">{checkout.symbol || "$"}{checkout.amount}</span></div>
              </div>
              {checkout.status === "mock" && (
                <div className="flex items-center gap-2 p-3 bg-warning/10 rounded-xl mb-4 text-xs text-warning">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" /><span>Demo Mode: No real payment charged. Click confirm to test.</span>
                </div>
              )}
              <div className="space-y-3">
                <Button data-testid="confirm-payment-btn" onClick={confirmPayment} disabled={processing} className="w-full btn-omni bg-primary text-primary-foreground gap-2">
                  {processing ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : "Confirm Payment"}
                </Button>
                <Button variant="ghost" onClick={() => setCheckout(null)} className="w-full rounded-full text-muted-foreground">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
